import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { RefreshCwIcon, SettingsIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable } from "@/components/data-table";
import { PageContainer, PageHeader } from "@/components/layout";
import DashboardInfoCard from "../../components/dashboard-info-card";
import FiltersCard from "../../components/filters-card";
import ContextSheet from "../../components/insights/context-sheet";
import InsightSheet from "../../components/insights/insight-sheet";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { Features } from "../../entities/features";
import { Permissions } from "../../entities/permissions";
import { INSIGHT_SCORES, type Insight, type InsightScore } from "../../entities/insights";
import { useFeaturesQuery, useIsFeatureEnabled } from "../../services/api/featuresApi";
import {
  useGenerateInsightsMutation,
  useInsightListQuery,
  useInsightQuery,
  useInsightSummaryQuery,
} from "../../services/api/insightsApi";
import { useQueryState } from "../../utils/query-state";
import { hasPermission } from "../login/selectors";
import generateColumns from "./columns";

const PAGE_SIZE = 15;

export default function Insights() {
  const { t, i18n } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const canView = useSelector(hasPermission(Permissions.PAGE_INSIGHTS_ROOT));
  const canManage = useSelector(hasPermission(Permissions.PAGE_INSIGHTS_MANAGE));
  const featureEnabled = useIsFeatureEnabled(Features.FT_INSIGHTS);
  // The flag's OWN loading state. useIsFeatureEnabled returns false while its
  // query is in flight, so the redirect below has to wait on that query — not,
  // as it did, on an unrelated summary query that can resolve first and bounce
  // a user whose feature is genuinely enabled.
  const { isLoading: featuresLoading } = useFeaturesQuery(undefined);

  const [page, setPage] = useState(0);
  // Staged locally, committed on Search — the convention every other filtered
  // page here uses, so typing doesn't refetch on every keystroke.
  const [stagedScore, setStagedScore] = useState<string>("all");
  const [stagedStatus, setStagedStatus] = useState<string>("open");
  const [filters, setFilters] = useState<Record<string, string>>({ status: "open" });
  const [contextOpen, setContextOpen] = useState(false);

  const [detail, setDetail] = useQueryState({
    paramName: "detail",
    fromQuery: (value: string | null) => value || null,
    toQuery: (value: string | null) => (value ? String(value) : ""),
    defaultValue: null,
  }) as [string | null, (next: string | null) => void];

  const { data, isFetching, isError, refetch } = useInsightListQuery({
    filters,
    range: { page: page + 1, limit: PAGE_SIZE },
  });
  const { data: summary, isLoading: summaryLoading } = useInsightSummaryQuery(undefined);
  const [generate, { isLoading: isGenerating }] = useGenerateInsightsMutation();

  const items: Insight[] = useMemo(() => data?.items ?? [], [data]);

  // Deep links land on a fingerprint that may not be on the current page, so
  // fall back to fetching it by id — the same pattern the resources page uses.
  const onPage = items.find((item) => item.fingerprint === detail);
  const { data: fetched, isFetching: fetchingDetail } = useInsightQuery(detail as string, {
    skip: !detail || Boolean(onPage),
  });
  const selected = onPage ?? fetched;

  const columns = useMemo(
    () => generateColumns({ t, language: i18n.language }),
    [t, i18n.language],
  );

  // Guard inside the page rather than by conditionally building the route:
  // a conditional route racing useFeaturesQuery gives a flash of 404.
  if (!canView || (!featuresLoading && !featureEnabled)) {
    return <Navigate to="/dashboard/app" replace />;
  }

  async function handleGenerate() {
    try {
      await generate(undefined).unwrap();
      showNotification(
        "info",
        t("insights.generate.started", "Analysis started. It takes a few minutes."),
      );
    } catch (error) {
      const status = (error as { status?: number })?.status;
      showNotification(
        "warning",
        status === 429
          ? t("insights.generate.too-soon", "An analysis ran recently. Try again later.")
          : t("insights.generate.error", "Could not start the analysis."),
      );
    }
  }

  function applyFilters() {
    const next: Record<string, string> = {};
    if (stagedScore !== "all") next.score = stagedScore;
    if (stagedStatus) next.status = stagedStatus;
    setFilters(next);
    setPage(0);
  }

  function clearFilters() {
    setStagedScore("all");
    setStagedStatus("open");
    setFilters({ status: "open" });
    setPage(0);
  }

  function filterByScore(score: InsightScore) {
    const next = filters.score === score ? "all" : score;
    setStagedScore(next);
    setFilters(next === "all" ? { status: stagedStatus } : { score: next, status: stagedStatus });
    setPage(0);
  }

  const lastUpdated = summary?.lastUpdated
    ? new Date(summary.lastUpdated).toLocaleString(i18n.language)
    : null;

  return (
    <PageContainer title={t("insights.tab-title", "Insights")}>
      <PageHeader
        title={t("insights.page-title", "Insights")}
        description={
          lastUpdated
            ? t("insights.last-updated", {
                defaultValue: "Last updated {{when}}",
                when: lastUpdated,
              })
            : t("insights.info-message", "Patterns found across your inspection media.")
        }
        actions={
          canManage ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setContextOpen(true)}>
                <SettingsIcon /> {t("insights.context.action", "Context")}
              </Button>
              <Button onClick={handleGenerate} disabled={isGenerating}>
                <RefreshCwIcon /> {t("insights.generate.action", "Run analysis")}
              </Button>
            </div>
          ) : null
        }
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryLoading
          ? Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-[120px] rounded-lg" />
            ))
          : (
            <>
              <DashboardInfoCard
                title={t("insights.cards.total", "Open insights")}
                value={String(summary?.total ?? 0)}
              />
              {INSIGHT_SCORES.map((score) => (
                <DashboardInfoCard
                  key={score}
                  title={t(`insights.score.${score}`, score)}
                  value={String(summary?.byScore?.[score] ?? 0)}
                  active={filters.score === score}
                  onClick={() => filterByScore(score)}
                />
              ))}
            </>
          )}
      </div>

      <div className="mt-6">
        <FiltersCard onSearch={applyFilters} onCancel={clearFilters}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("insights.filter.score", "Severity")}
              </label>
              <Select value={stagedScore} onValueChange={setStagedScore}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all", "All")}</SelectItem>
                  {INSIGHT_SCORES.map((score) => (
                    <SelectItem key={score} value={score}>
                      {t(`insights.score.${score}`, score)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("insights.filter.status", "Status")}
              </label>
              <Select value={stagedStatus} onValueChange={setStagedStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">{t("insights.status.open", "To review")}</SelectItem>
                  <SelectItem value="validated">
                    {t("insights.status.validated", "Confirmed")}
                  </SelectItem>
                  <SelectItem value="dismissed">
                    {t("insights.status.dismissed", "Dismissed")}
                  </SelectItem>
                  <SelectItem value="all">{t("common.all", "All")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </FiltersCard>
      </div>

      {isError ? (
        <Alert variant="destructive" className="mt-6">
          <AlertTitle>{t("insights.error.title", "Could not load insights")}</AlertTitle>
          <AlertDescription>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t("common.retry", "Retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="mt-6">
          <DataTable
            columns={columns}
            data={items}
            loading={isFetching}
            skeletonRows={8}
            onRowClick={(row: Insight) => setDetail(row.fingerprint)}
            manualPagination={{
              pageIndex: page,
              pageCount: Math.ceil((data?.itemCount ?? 0) / PAGE_SIZE),
              onPageChange: setPage,
            }}
            emptyMessage={
              // "Never ran" and "ran, found nothing" are very different
              // messages, and both are common until media volume grows.
              summary?.reason === "never-run"
                ? t("insights.empty.never-run", "No analysis has run yet.")
                : summary?.reason === "insufficient-data"
                  ? t(
                      "insights.empty.insufficient-data",
                      "Not enough inspection media yet to find patterns.",
                    )
                  : t("insights.empty.default", "No insights match these filters.")
            }
          />
        </div>
      )}

      <InsightSheet
        open={Boolean(detail)}
        onOpenChange={(open) => !open && setDetail(null)}
        insight={selected}
        loading={fetchingDetail}
      />

      {canManage ? <ContextSheet open={contextOpen} onOpenChange={setContextOpen} /> : null}
    </PageContainer>
  );
}
